import { extractErrorMessages } from './extractErrorMessages';

export type FacebookPermissionLevel = 'required' | 'recommended' | 'optional';

export interface FacebookPermission {
    /** Meta permission name, e.g. `leads_retrieval`. Never translated. */
    name: string;
    level: FacebookPermissionLevel;
    /** i18n key (namespace `crm`) of the short "why we need it" description. */
    descriptionKey: string;
}

const permission = (name: string, level: FacebookPermissionLevel): FacebookPermission => ({
    name,
    level,
    descriptionKey: `campaigns.facebookSetup.permissions.${name}`,
});

/** The System User token must carry all of these (contract: "Permissions"). */
export const FACEBOOK_REQUIRED_PERMISSIONS: readonly FacebookPermission[] = [
    permission('pages_show_list', 'required'),
    permission('pages_read_engagement', 'required'),
    permission('pages_manage_ads', 'required'),
    permission('leads_retrieval', 'required'),
];

/** Missing ones only produce a warning. */
export const FACEBOOK_RECOMMENDED_PERMISSIONS: readonly FacebookPermission[] = [
    permission('ads_read', 'recommended'),
];

/** Only needed when pages are assigned through a client business. */
export const FACEBOOK_OPTIONAL_PERMISSIONS: readonly FacebookPermission[] = [
    permission('business_management', 'optional'),
];

export const FACEBOOK_PERMISSIONS: readonly FacebookPermission[] = [
    ...FACEBOOK_REQUIRED_PERMISSIONS,
    ...FACEBOOK_RECOMMENDED_PERMISSIONS,
    ...FACEBOOK_OPTIONAL_PERMISSIONS,
];

export function findFacebookPermission(name: string): FacebookPermission | undefined {
    return FACEBOOK_PERMISSIONS.find(item => item.name === name);
}

/**
 * Names from `wanted` (default: the required ones) that are absent from `granted`,
 * in `wanted` order. `granted` may be missing/null (token not inspected yet).
 */
export function computeMissingPermissions(
    granted: readonly string[] | null | undefined,
    wanted: readonly string[] = FACEBOOK_REQUIRED_PERMISSIONS.map(item => item.name)
): string[] {
    const have = new Set(granted ?? []);
    return wanted.filter(name => !have.has(name));
}

export interface FacebookConnectError {
    errors: string[];
    missingPermissions: string[];
}

/**
 * Reads the 422 body of `POST /crm/campaigns/facebook/connect`
 * (`{ errors: string[], missing_permissions?: string[] }`) from an axios-style error.
 */
export function parseFacebookConnectError(error: unknown): FacebookConnectError {
    const body = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data;
    const missing =
        body && typeof body === 'object'
            ? (body as { missing_permissions?: unknown }).missing_permissions
            : undefined;

    return {
        errors: extractErrorMessages(body),
        missingPermissions: Array.isArray(missing)
            ? missing.filter((item): item is string => typeof item === 'string')
            : [],
    };
}

/**
 * Normalises a Meta token expiry to a Date, or `null` when it never expires.
 * Accepts `null`/`0` (never), unix seconds (Meta's `debug_token` format, number or
 * numeric string) and ISO date strings (the status endpoint's `token_expires_at`).
 */
export function parseFacebookExpiry(value: string | number | null | undefined): Date | null {
    if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
        return null;
    }
    const asNumber = typeof value === 'number' ? value : /^\d+$/.test(value) ? Number(value) : NaN;
    const date = Number.isNaN(asNumber) ? new Date(value) : new Date(asNumber * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
}
